import { format, getDay, parse, startOfWeek } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import React, { useEffect, useState } from 'react';
import { Calendar, CalendarProps, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import Toolbar from '../SFCCalendar/CustomComponents/Toolbar';
import { Event, eventStyleGetter } from '../SFCCalendar/SFCCalendar';
import './SchedulerCalendar.css';

const locales = {
  'en-US': enUS
};

// Create the localizer
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales
});

interface SchedulerCalendarProps {
  daysOfWeek: number[];
  startDate?: Date;
  endDate?: Date;
  noClassDates: string[];
  /** When set (edit), green days come from class cards instead of the recurrence inputs. */
  classDates?: string[];
}

export const SchedulerCalendar: React.FC<SchedulerCalendarProps> = ({
  daysOfWeek,
  noClassDates,
  startDate,
  endDate,
  classDates
}) => {
  const [visibleDate, setVisibleDate] = useState(startDate ?? new Date());
  const validDaysOfWeek = daysOfWeek.filter((day) => day >= 0 && day <= 6);
  const classDateSet = classDates !== undefined ? new Set(classDates) : null;

  useEffect(() => {
    if (startDate) {
      setVisibleDate(startDate);
    }
  }, [startDate]);

  // Calendar dates only, no timezone conversion. react-big-calendar hands us a
  // local Date per cell and getDay reads local. Converting to a UTC instant via
  // dateInTimeZone and then calling getDay compares two different frames, which
  // shifts the weekday for any user whose configured tz is east of their browser.
  const dayPropGetter: CalendarProps<Event>['dayPropGetter'] = (date) => {
    const ds = format(date, 'yyyy-MM-dd');
    const noClassStyle = {
      backgroundColor: '#B81F69',
      boxShadow: `-1px -1px 4px 0 rgba(0, 0, 0, .25) inset, 1px 1px 4px 1px rgba(255, 255, 255, .35) inset`
    };
    const classStyle = {
      backgroundColor: '#B4C634',
      boxShadow: `-1px -1px 4px 0 rgba(0, 0, 0, .25) inset, 1px 1px 4px 1px rgba(255, 255, 255, .35) inset`
    };

    // Edit: active class cards win so a leftover no-class label cannot hide a real card.
    if (classDateSet) {
      if (classDateSet.has(ds)) return { style: classStyle };
      if (noClassDates.includes(ds)) return { style: noClassStyle };
      return {};
    }

    // Create: no-class wins over the weekday preview (no cards exist for those dates).
    if (noClassDates.includes(ds)) return { style: noClassStyle };
    if (
      startDate &&
      endDate &&
      validDaysOfWeek.includes(getDay(date)) &&
      ds >= format(startDate, 'yyyy-MM-dd') &&
      ds <= format(endDate, 'yyyy-MM-dd')
    ) {
      return { style: classStyle };
    }
    return {};
  };

  return (
    <div
      className="m-auto p-4"
      style={{ background: '#FAFAFA', maxWidth: 400 }}
    >
      <Calendar<Event>
        localizer={localizer}
        startAccessor="start"
        endAccessor="end"
        className="scheduler-calendar"
        views={['month']} // Only show the month view
        defaultView="month" // Set the default view to month
        style={{ height: 400, background: '#FAFAFA' }}
        components={{
          toolbar: Toolbar,
          month: {
            header: (props) => <>{localizer.format(props.date, 'EE')}</>
          }
        }}
        date={visibleDate}
        onNavigate={setVisibleDate}
        dayPropGetter={dayPropGetter}
        eventPropGetter={eventStyleGetter}
      />
    </div>
  );
};
